import { calculateDistance, calculateCenter } from "@perceptron/utils/math";
import { create } from "zustand";

import { CaretBubblePosition } from "@demo/extensions/caret-bubble";
import { Role } from "@demo/types/base";

// State machine states for caret bubble visibility
export type CaretBubbleState = "hidden" | "showing";

// Marks the caret bubble button, so blur handling can tell focus moving onto the bubble apart from focus moving to
// other fixed-position chrome
export const CARET_BUBBLE_ATTRIBUTE = "data-caret-bubble";

interface CaretBubbleStore {
  // State machine state
  state: CaretBubbleState;

  // Which editor currently has the active caret bubble
  activeEditor: Role | null;

  // The caret position in the active editor
  caretPosition: CaretBubblePosition | null;

  // Mouse position for proximity detection
  mousePosition: { x: number; y: number } | null;

  // Which editor has focus (null when none does)
  focusedEditor: Role | null;

  // Whether the caret bubble button itself has keyboard focus
  isBubbleFocused: boolean;

  // State machine transition
  transition: (to: CaretBubbleState) => void;

  // Set the active editor and position
  setCaretPosition: (editor: Role | null, position: CaretBubblePosition | null) => void;

  // Update mouse position
  setMousePosition: (position: { x: number; y: number } | null) => void;

  // Clear the caret bubble
  clearCaretBubble: () => void;

  // Whether the bubble belongs in the DOM for an editor (so it can be reached with Tab), regardless of the mouse
  isAvailableForEditor: (editor: Role, isDisabled: boolean) => boolean;

  // Whether the bubble is shown to the pointer: available, and the mouse is near the caret
  isVisibleForEditor: (editor: Role, isDisabled: boolean) => boolean;

  // Handle focus events
  handleEditorFocus: (editor: Role) => void;

  // Handle blur events
  handleEditorBlur: (editor: Role, relatedTarget: HTMLElement | null) => void;

  // Track keyboard focus on the bubble button
  setBubbleFocused: (focused: boolean) => void;
}

const MOUSE_PROXIMITY_THRESHOLD = 350;

type BubbleSnapshot = Pick<
  CaretBubbleStore,
  "state" | "activeEditor" | "caretPosition" | "focusedEditor" | "isBubbleFocused"
>;

// Store-only checks for whether an editor has a bubble to offer; returns its caret position when it does. The
// open-dialog DOM query is left to callers to run last: these selectors re-run on every store write, including the
// per-frame mouse position updates.
const getBubbleCaret = (snapshot: BubbleSnapshot, editor: Role, isDisabled: boolean): CaretBubblePosition | null => {
  const { activeEditor, caretPosition, state, focusedEditor, isBubbleFocused } = snapshot;

  if (activeEditor !== editor || isDisabled) return null;
  if (!caretPosition || !caretPosition.isValidTarget || state === "hidden") return null;

  // No bubble next to a blurred editor. Blur hides it, but the caret-bubble plugin reports a still-valid position
  // right after the blur, which would otherwise bring it straight back.
  if (focusedEditor !== editor && !isBubbleFocused) return null;

  return caretPosition;
};

const noOpenDialog = () => document.querySelector('[role="dialog"]') === null;

export const useCaretBubbleStore = create<CaretBubbleStore>((set, get) => ({
  state: "hidden",
  activeEditor: null,
  caretPosition: null,
  mousePosition: null,
  focusedEditor: null,
  isBubbleFocused: false,

  transition: (to) => {
    set({ state: to });
  },

  setCaretPosition: (editor, position) => {
    const { transition, activeEditor } = get();

    // If switching editors, always update activeEditor
    const isEditorSwitch = editor !== activeEditor;

    // Update position and transition to showing state if appropriate
    if (editor && position && position.isValidTarget) {
      transition("showing");

      set({
        activeEditor: editor,
        caretPosition: position,
      });
    } else if (editor === activeEditor || isEditorSwitch) {
      // Update position even if invalid, but only for the active editor
      // or when switching editors
      set({
        activeEditor: editor,
        caretPosition: position,
      });

      // Hide bubble if position is invalid
      if (!position?.isValidTarget) {
        transition("hidden");
      }
    }
  },

  setMousePosition: (position) => {
    set({ mousePosition: position });
  },

  clearCaretBubble: () => {
    const { transition, state } = get();

    // Only transition if not already hidden
    if (state !== "hidden") {
      transition("hidden");
    }

    set({
      activeEditor: null,
      caretPosition: null,
      isBubbleFocused: false,
      // Note: We intentionally don't clear mousePosition here
      // This allows the bubble to work properly when switching between editors
    });
  },

  handleEditorFocus: (editor) => {
    set({ focusedEditor: editor });
  },

  handleEditorBlur: (editor, relatedTarget) => {
    const { activeEditor, focusedEditor } = get();
    const isActive = activeEditor === editor;

    // Tabbing from the editor onto its bubble keeps the bubble available. Mark the bubble focused in this same update
    // so it isn't unmounted between the editor's blur and the button's own focus event.
    const focusMovedToBubble = isActive && !!relatedTarget?.closest(`[${CARET_BUBBLE_ATTRIBUTE}]`);

    set({
      ...(focusedEditor === editor ? { focusedEditor: null } : {}),
      ...(focusMovedToBubble ? { isBubbleFocused: true } : {}),
      // Hide immediately on blur
      ...(isActive && !focusMovedToBubble ? { state: "hidden" as const } : {}),
    });
  },

  setBubbleFocused: (focused) => {
    set({ isBubbleFocused: focused });
  },

  isAvailableForEditor: (editor, isDisabled = false) => {
    return getBubbleCaret(get(), editor, isDisabled) !== null && noOpenDialog();
  },

  isVisibleForEditor: (editor, isDisabled = false) => {
    const caret = getBubbleCaret(get(), editor, isDisabled);
    if (!caret) return false;

    // Show by default if no mouse position (e.g., just switched editors)
    const { mousePosition } = get();
    if (mousePosition) {
      // Check mouse proximity to cursor position
      const cursorX = caret.cursorX || caret.lineEndX;
      const cursorY = calculateCenter(caret.lineCoords.top, caret.lineCoords.bottom);

      if (calculateDistance(mousePosition.x, mousePosition.y, cursorX, cursorY) > MOUSE_PROXIMITY_THRESHOLD) {
        return false;
      }
    }

    return noOpenDialog();
  },
}));
